import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import {
  getSettings,
  updateSettings,
} from "../../../../../../../services/settingsServices";

import { getAllGalleries } from "../../../../../../../services/gallerieServices";

import {
  uploadSettingsHeader1,
  uploadSettingsHeader2,
} from "../../../../../../../services/settingsPhotoServices";

import {
  DEFAULT_HOME_SETTINGS,
  buildHeaderImages,
  calculateHomeContentStats,
  extractUploadedHeaderUrl,
  getActiveCarouselPhotos,
  getSelectedGallery,
  normalizeHomeSettingsPayload,
  unwrapSettings,
  validateImageFile,
} from "../utils/homeContentSettingsUtils";

export function useHomeContentSettings() {
  const [settings, setSettings] = useState(DEFAULT_HOME_SETTINGS);
  const [galleries, setGalleries] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [previewDark, setPreviewDark] = useState(false);
  const [slide, setSlide] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);

  const intervalRef = useRef(null);

  const [uploadDrawerOpen, setUploadDrawerOpen] = useState(false);
  const [uploadTarget, setUploadTarget] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [imgVersion, setImgVersion] = useState({
    header1: 0,
    header2: 0,
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const [settingsData, galleriesData] = await Promise.all([
        getSettings(),
        getAllGalleries(),
      ]);

      const first = unwrapSettings(settingsData);

      if (first) {
        setSettings({
          ...DEFAULT_HOME_SETTINGS,
          ...first,
        });
      }

      setGalleries(Array.isArray(galleriesData) ? galleriesData : []);

      setImgVersion((prev) => ({
        header1: prev.header1 + 1,
        header2: prev.header2 + 1,
      }));
    } catch (error) {
      console.error("Erreur chargement page accueil :", error);
      toast.error("Impossible de charger les paramètres de la page d’accueil.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const headerImages = useMemo(() => {
    return buildHeaderImages(settings, imgVersion);
  }, [settings, imgVersion]);

  const selectedGallery = useMemo(() => {
    return getSelectedGallery(galleries, settings.carrousel_id);
  }, [galleries, settings.carrousel_id]);

  const carrouselPhotos = useMemo(() => {
    return getActiveCarouselPhotos(selectedGallery);
  }, [selectedGallery]);

  const stats = useMemo(() => {
    return calculateHomeContentStats(settings, galleries, selectedGallery);
  }, [settings, galleries, selectedGallery]);

  useEffect(() => {
    if (!autoPlay) return undefined;

    clearInterval(intervalRef.current);

    intervalRef.current = setInterval(() => {
      setSlide((current) => (current + 1) % headerImages.length);
    }, 8000);

    return () => clearInterval(intervalRef.current);
  }, [autoPlay, headerImages.length]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setSettings((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "header_url1") {
      setImgVersion((prev) => ({
        ...prev,
        header1: prev.header1 + 1,
      }));
    }

    if (name === "header_url2") {
      setImgVersion((prev) => ({
        ...prev,
        header2: prev.header2 + 1,
      }));
    }
  };

  const handleContentChange = (name, value) => {
    setSettings((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const saveSettings = async (event) => {
    event?.preventDefault?.();

    try {
      setSaving(true);

      const payload = normalizeHomeSettingsPayload(settings);

      await updateSettings(payload);

      setSettings(payload);
      toast.success("Page d’accueil mise à jour avec succès.");
    } catch (error) {
      console.error("Erreur sauvegarde page accueil :", error);
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data ||
          "Erreur lors de la mise à jour de la page d’accueil."
      );
    } finally {
      setSaving(false);
    }
  };

  const openUploadDrawer = (target) => {
    setUploadTarget(target);
    setUploadProgress(0);
    setUploadDrawerOpen(true);
  };

  const closeUploadDrawer = () => {
    if (uploading) return;

    setUploadDrawerOpen(false);
    setUploadTarget(null);
    setUploadProgress(0);
  };

  const handleUploadHeader = async ({ file, outputFormat = "webp" }) => {
    if (!uploadTarget) {
      toast.error("Cible d’upload inconnue.");
      return;
    }

    const fileError = validateImageFile(file);

    if (!file) {
      toast.error("Veuillez sélectionner une image.");
      return;
    }

    if (fileError) {
      toast.error(fileError);
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      const uploadFn =
        uploadTarget === "header1"
          ? uploadSettingsHeader1
          : uploadSettingsHeader2;

      const uploaded = await uploadFn(file, {
        format: outputFormat,
        onUploadProgress: (event) => {
          if (!event.total) return;
          setUploadProgress(Math.round((event.loaded * 100) / event.total));
        },
      });

      const newUrl = extractUploadedHeaderUrl(uploaded);

      if (!newUrl) {
        toast.error("Réponse upload invalide : URL manquante.");
        return;
      }

      if (uploadTarget === "header1") {
        setSettings((prev) => ({
          ...prev,
          header_url1: newUrl,
        }));

        setImgVersion((prev) => ({
          ...prev,
          header1: prev.header1 + 1,
        }));
      } else {
        setSettings((prev) => ({
          ...prev,
          header_url2: newUrl,
        }));

        setImgVersion((prev) => ({
          ...prev,
          header2: prev.header2 + 1,
        }));
      }

      toast.success("Image du header mise à jour.");
      setUploadDrawerOpen(false);
      setUploadTarget(null);
    } catch (error) {
      console.error("Erreur upload header :", error);
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data ||
          "Erreur lors du téléversement de l’image."
      );
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  return {
    settings,
    galleries,
    selectedGallery,
    carrouselPhotos,
    headerImages,

    loading,
    saving,

    stats,

    previewDark,
    setPreviewDark,
    slide,
    setSlide,
    autoPlay,
    setAutoPlay,

    handleChange,
    handleContentChange,
    saveSettings,
    fetchData,

    uploadDrawerOpen,
    uploadTarget,
    uploading,
    uploadProgress,
    openUploadDrawer,
    closeUploadDrawer,
    handleUploadHeader,
  };
}